import React, { FC, useContext, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyLabTemplate } from 'apollo/templates/organization';
import { useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { LabFormData } from 'models/forms';
import LabForm from './lab-form.component';
import { Lab } from 'models/lab';
import { setupVismaCodingIdsTemplate } from 'apollo/templates/visma';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

type EditLabDialogProps = {
    dialogOpen: boolean;
    lab: Lab | undefined;
    onCloseDialog: () => void;
    onLabModified: () => void;
};
const EditLabDialog: FC<EditLabDialogProps> = ({
    dialogOpen,
    lab,
    onCloseDialog,
    onLabModified,
}: EditLabDialogProps) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [currentLab, setCurrentLab] = useState<Lab>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);

    const [modifyLab] = useMutation(modifyLabTemplate, {
        onCompleted: ({}) => {
            onLabModified();
            setIsLoading(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [setupVismaCodingIds, { loading: isVismaSetupLoading }] = useMutation(setupVismaCodingIdsTemplate);

    const isVismaEnabledForLab = Boolean(
        customerSettings.getCustomerSetting(currentLab?.customerSettings ?? [], CONSTANTS.SETTINGS_NAMES.VISMA),
    );
    const labVismaCodingIds = currentLab?.vismaCodingIds;
    const isVismaCodingIdsMissing =
        !labVismaCodingIds ||
        !labVismaCodingIds.unitId ||
        !labVismaCodingIds.noVatCodingId ||
        !labVismaCodingIds.vatCodingId;

    const showToast = (severity: 'success' | 'error', message: string) =>
        setAppToasterContent({ isOpen: true, severity, message });

    const handleSetupVismaCodingIds = async () => {
        if (!currentLab?._id) return;
        try {
            const result = await setupVismaCodingIds({ variables: { labId: currentLab._id } });
            const updated = result.data?.setupVismaCodingIds;
            if (updated) {
                setCurrentLab({ ...currentLab, vismaCodingIds: updated });
            }
            showToast('success', translate('organization:VISMA_CODING_IDS_SUCCESS'));
        } catch (err) {
            showBoundary(err);
        }
    };

    useEffect(() => {
        setCurrentLab(lab);
        setIsLoading(false);
    }, [lab]);

    const handleCloseDialog = () => {
        setErrorMessage(undefined);
        setIsLoading(false);
        onCloseDialog();
    };

    const handleFormSubmitted = (formData: LabFormData) => {
        setIsLoading(true);
        modifyLab({
            variables: {
                labId: currentLab?._id,
                lab: {
                    active: formData?.active,
                    name: formData?.name,
                    officialName: formData.officialName,
                    organizationNumber: formData.organizationNumber,
                    contact: {
                        address: formData?.address,
                        communication: {
                            phone: formData?.phone,
                            email: formData?.email,
                            site: formData?.site,
                        },
                    },
                    crediFlow: {
                        partyID: formData?.crediflowPartyId,
                    },
                    localeFormat: formData.localeFormat,
                },
            },
        });
    };

    return (
        <Dialog fullWidth maxWidth="md" open={dialogOpen} onClose={handleCloseDialog}>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1}>{translate('organization:UPDATE_LAB')}</Box>
                    <Box>
                        <Typography color="gray" variant="caption">
                            {currentLab?._id}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
                {!isLoading && currentLab ? (
                    <>
                        <LabForm
                            lab={currentLab}
                            onClose={handleCloseDialog}
                            onFormSubmitted={handleFormSubmitted}
                            submitButtonText={translate('organization:UPDATE_LAB')}
                        />
                        {isVismaEnabledForLab && (
                            <>
                                <Divider sx={{ mt: 3, mb: 3 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                        {translate('organization:VISMA_SETUP_TITLE')}
                                    </Typography>
                                    {isVismaCodingIdsMissing ? (
                                        <Box>
                                            <LoadingButton
                                                onClick={handleSetupVismaCodingIds}
                                                loading={isVismaSetupLoading}
                                            >
                                                {translate('organization:FETCH_VISMA_CODING_IDS')}
                                            </LoadingButton>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {translate('organization:VISMA_CODING_IDS_CONFIGURED')}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'auto 1fr',
                                                    columnGap: 2,
                                                    rowGap: 0.5,
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {translate('organization:VISMA_UNIT_ID')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {labVismaCodingIds!.unitId}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {translate('organization:VISMA_NO_VAT_CODING_ID')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {labVismaCodingIds!.noVatCodingId}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {translate('organization:VISMA_VAT_CODING_ID')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {labVismaCodingIds!.vatCodingId}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EditLabDialog;
